import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { BlockKind, BlockRule, BlockStats } from "shared";
import { ApiException } from "@/api/client";
import { createBlock, deleteBlock, listBlocks } from "@/api/blocks";
import { formatDt } from "@/lib/format";

export function BlocksPage() {
	const [blocks, setBlocks] = useState<BlockRule[]>([]);
	const [stats, setStats] = useState<BlockStats | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [kind, setKind] = useState<BlockKind>("user_agent");
	const [value, setValue] = useState("");
	const [note, setNote] = useState("");
	const [busy, setBusy] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await listBlocks();
			setBlocks(res.blocks);
			setStats(res.stats);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Failed to load",
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	async function onCreate(e: FormEvent) {
		e.preventDefault();
		setFormError(null);
		const trimmed = value.trim();
		if (!trimmed) {
			setFormError("Value is required");
			return;
		}
		setBusy(true);
		try {
			const { block } = await createBlock({
				kind,
				value: trimmed,
				note: note.trim() || null,
			});
			setBlocks((prev) => [block, ...prev]);
			setStats((prev) =>
				prev
					? {
							total: prev.total + 1,
							byIp: prev.byIp + (kind === "ip" ? 1 : 0),
							byUserAgent:
								prev.byUserAgent + (kind === "user_agent" ? 1 : 0),
						}
					: prev,
			);
			setValue("");
			setNote("");
		} catch (err) {
			setFormError(
				err instanceof ApiException ? err.message : "Create failed",
			);
		} finally {
			setBusy(false);
		}
	}

	async function onDelete(block: BlockRule) {
		if (
			!confirm(
				`Delete ${block.kind === "ip" ? "IP" : "UA"} rule for "${block.value}"?`,
			)
		)
			return;
		try {
			await deleteBlock(block.id);
			setBlocks((prev) => prev.filter((b) => b.id !== block.id));
			setStats((prev) =>
				prev
					? {
							total: Math.max(0, prev.total - 1),
							byIp: Math.max(0, prev.byIp - (block.kind === "ip" ? 1 : 0)),
							byUserAgent: Math.max(
								0,
								prev.byUserAgent - (block.kind === "user_agent" ? 1 : 0),
							),
						}
					: prev,
			);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Delete failed",
			);
		}
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-xl font-semibold tracking-tight">Blocks</h1>
				{stats && (
					<p className="mt-1 text-sm text-fg-muted">
						{stats.total} rules · {stats.byIp} IP · {stats.byUserAgent} UA
					</p>
				)}
				<p className="mt-1 text-xs text-fg-muted">
					Blocked requests get a 403 page and are not counted as clicks.
					IP matches exactly (CF-Connecting-IP); UA matches as a
					case-insensitive substring.
				</p>
			</div>

			<form
				onSubmit={onCreate}
				className="space-y-3 rounded-xl border border-border bg-surface-raised p-5"
			>
				<h2 className="text-sm font-medium">Add rule</h2>
				<div className="flex flex-wrap gap-3">
					<label className="text-sm">
						<span className="text-fg-muted">Kind</span>
						<select
							value={kind}
							onChange={(e) => setKind(e.target.value as BlockKind)}
							className="mt-1 block rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
						>
							<option value="user_agent">User-agent substring</option>
							<option value="ip">IP (exact)</option>
						</select>
					</label>
					<label className="block flex-1 min-w-[12rem] text-sm">
						<span className="text-fg-muted">
							{kind === "ip" ? "IP address" : "UA substring"}
						</span>
						<input
							type="text"
							required
							placeholder={
								kind === "ip" ? "203.0.113.66" : "ahrefsbot"
							}
							value={value}
							onChange={(e) => setValue(e.target.value)}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
				</div>
				<label className="block text-sm">
					<span className="text-fg-muted">Note (optional)</span>
					<input
						type="text"
						maxLength={256}
						placeholder="e.g. Ahrefs crawler"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
					/>
				</label>
				{formError && (
					<p className="text-xs text-rose-600 dark:text-rose-400">
						{formError}
					</p>
				)}
				<button
					type="submit"
					disabled={busy}
					className="rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
				>
					Add rule
				</button>
			</form>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<div className="overflow-x-auto rounded-xl border border-border">
				<table className="w-full min-w-[640px] text-left text-sm">
					<thead className="border-b border-border bg-surface-raised text-fg-muted">
						<tr>
							<th className="px-3 py-2 font-medium">Kind</th>
							<th className="px-3 py-2 font-medium">Value</th>
							<th className="px-3 py-2 font-medium">Scope</th>
							<th className="px-3 py-2 font-medium">Note</th>
							<th className="px-3 py-2 font-medium">Created</th>
							<th className="px-3 py-2 font-medium">Actions</th>
						</tr>
					</thead>
					<tbody>
						{blocks.map((block) => (
							<tr
								key={block.id}
								className="border-b border-border last:border-0"
							>
								<td className="px-3 py-2">
									<span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs font-medium">
										{block.kind === "ip" ? "IP" : "UA"}
									</span>
								</td>
								<td className="px-3 py-2 font-mono text-xs break-all">
									{block.value}
								</td>
								<td className="px-3 py-2 text-xs text-fg-muted">
									{block.linkId === null ? "Global" : "Per-link"}
								</td>
								<td className="max-w-[14rem] truncate px-3 py-2 text-xs text-fg-muted">
									{block.note ?? "—"}
								</td>
								<td className="px-3 py-2 text-xs text-fg-muted whitespace-nowrap">
									{formatDt(block.createdAt)}
								</td>
								<td className="px-3 py-2">
									<button
										type="button"
										className="text-xs text-rose-600 hover:underline dark:text-rose-400"
										onClick={() => void onDelete(block)}
									>
										Delete
									</button>
								</td>
							</tr>
						))}
						{!loading && blocks.length === 0 && (
							<tr>
								<td
									colSpan={6}
									className="px-3 py-8 text-center text-fg-muted"
								>
									No block rules. Add one above.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{loading && <p className="text-sm text-fg-muted">Loading…</p>}
		</div>
	);
}
